<template lang="pug">
.schedule-table-wrap
  //- App-specific tabs for Annual/Monthly filtering
  .app-schedule-tabs(v-if="isAppContext" role="tablist" aria-label="Challenge schedule type")
    button.tab(
      type="button"
      role="tab"
      :aria-selected="activeTab === 'annual'"
      :class="{ active: activeTab === 'annual' }"
      @click="activeTab = 'annual'"
    ) {{ __('Annual') }}
    button.tab(
      type="button"
      role="tab"
      :aria-selected="activeTab === 'monthly'"
      :class="{ active: activeTab === 'monthly' }"
      @click="activeTab = 'monthly'"
    ) {{ __('Monthly') }}

  //- Challenge cards for app context
  .app-challenges-list(v-if="isAppContext")
    .challenge-card(v-for="row in filteredRows" :key="row.title")
      .challenge-info
        h3.challenge-name {{ row.title }}
      .challenge-action
        RogueButton(v-if="row.challengeStatus === 'active'" color="red" width="full" @click="openInBrowser(row.registerLink)") {{ __('REGISTER') }}
        RogueButton.registered(v-else-if="row.challengeStatus === 'registered'" :link="row.detailsLink" width="full") {{ __('REGISTERED') }}
        RogueButton.register-soon(
          v-else-if="row.challengeStatus === 'register-soon'"
          color="gray"
          width="full"
          :disabled="true"
          :style="{ background: '#e5e5e5', color: '#333', border: '1px solid #ccc' }"
        ) {{ __('Coming Soon') }}
        RogueButton(v-else-if="row.challengeStatus === 'closed'" color="gray" width="full" @click="openInBrowser(row.challengeLink)") {{ __('COMPLETE') }}

  //- Original table layout for web
  table.schedule-table(v-else)
    tbody
      tr.heading
        td.th-1
          h4 {{__('Challenge')}}
        td.th-2
          h4 {{__('Description')}}
        td.th-3
          h4 {{__('Start Date')}}
        td.th-4
          h4 {{__('End Date')}}
        td.th-5
          h4 {{__('Register')}}
      tr.rows(v-for="row in rows")
        td.td-1
          p {{row.title}}
          .description-box
            RogueLink.details(:to="row.detailsLink" v-if="row.detailsLink") {{__('Details')}}
            p.mobile-description {{row.description}}
        td.td-2
          p {{row.description}}
        td.td-3
          p {{row.startDate}}
        td.td-4
          p {{row.endDate}}
        td
          RogueButton(v-if="row.challengeStatus === 'active'" :link="row.registerLink" color="red") {{__('REGISTER')}}
          RogueButton.register-soon(v-if="row.challengeStatus === 'register-soon'" :link="row.registerLink" color="gray") {{row.registerDate}}
          RogueButton(v-if="row.challengeStatus === 'closed'" :link="row.challengeLink" color="gray") {{__('COMPLETE')}}
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRogue } from '../../js/rogue'

const props = withDefaults(defineProps<{
  rows?: {
    title: string,
    description: string,
    startDate: string,
    endDate: string,
    challengeStatus: string,
    detailsLink?: string,
    registerLink?: string,
    registerDate?: string,
    challengeLink?: string,
  }[];
}>(), {
  rows: () => [],
})

const rogue = useRogue()

// Check if we're in app context
const isAppContext = computed(() => !!rogue?.appContext)

// Open link in external browser (for app context)
function openInBrowser(url: string | undefined) {
  if (url && rogue?.emitToApp) {
    rogue.emitToApp('open_in_browser', { url })
  }
}

// Active tab for app view
const activeTab = ref<'annual' | 'monthly'>('annual')

// Status priority for sorting (lower number = higher priority)
const statusPriority: Record<string, number> = {
  'active': 1,
  'closed': 2,
  'register-soon': 3,
}

// Filter rows based on active tab and sort by status priority
// Annual challenges have "Yearly" as their endDate in the CMS
const filteredRows = computed(() => {
  if (!isAppContext.value) {
    return props.rows
  }

  const filtered = props.rows.filter(row => {
    const isAnnual = row.endDate?.toLowerCase() === 'yearly'

    if (activeTab.value === 'annual') {
      return isAnnual
    } else {
      return !isAnnual
    }
  })

  // Sort by status priority: active (REGISTER) -> closed (COMPLETE) -> register-soon (COMING SOON)
  return filtered.sort((a, b) => {
    const priorityA = statusPriority[a.challengeStatus] ?? 99
    const priorityB = statusPriority[b.challengeStatus] ?? 99
    return priorityA - priorityB
  })
})
</script>

<style lang="scss" scoped>
.schedule-table-wrap {
  max-width: 100%;
  overflow-x: auto;
}

table.schedule-table {
  text-align: left;
  border: 2px solid $color-gray-x-light;

  .heading {
    display: table;
    background: $color-gray-x-light !important;
    text-align: left;
    padding: $size-xl $size-l;
    border-bottom: none;
    width: 100%;

    @include not-desktop() {
      padding: $size-m $size-s;
    }
  }

  .heading h4 {
    margin-bottom: 0;
  }

  .heading td:first-child,
  .rows td:first-child {
    font-weight: normal;
  }

  .heading td, .rows td {
    border-bottom: none;
    text-align: left;
    width: 20%;
    padding: 0;
    min-width: 132px;

    @include mobile() {
      width: 50%;
    }
  }

  .heading td:not(:last-child), .rows td:not(:last-child) {
    padding-right: $space-s;
  }

  .rows td:not(:first-child){
    @include mobile() {
      padding: 0 0 0 $space-s;
    }
  }

  .heading .th-5{
    @include mobile() {
      padding: $size-xxs;
    }
  }

  .heading .th-2, .heading .th-3, .heading .th-4,
  .rows .td-2, .rows .td-3, .rows .td-4 {
    @include mobile() {
      display: none;
    }
  }

  .rows {
    display: table;
    align-items: center;
    padding: $size-xxs $size-l;
    width: 100%;
    @include mobile() {
      padding: $size-xxs $size-m;
    }
  }

  .rows p {
    font-family: $font-condensed;
    font-size: $size-s;
    margin-bottom: 0;
  }

  .description-box{
    display: flex;
    flex-direction: column;
    @include mobile() {
      flex-direction: column-reverse;
    }
  }

  .rows .details {
    color: $color-black;
    margin-top: $space-xs;
  }

  .rows button {
    min-width: 124px;
    font-weight: bold;
    width: 100%;

    @include not-desktop() {
      padding: $size-xxs;
      width: 100%;
    }

    @include not-mobile() {
      min-width: 132px;
    }
  }

  .rows button.register-soon {
    pointer-events: none;
  }

  .rows:nth-of-type(odd), .rows:nth-of-type(odd) td, .heading td:first-child {
    background: $color-gray-x-light !important;
  }

  .rows .mobile-description{
    font-size: $size-xxs;
    font-style: italic;

    @include not-mobile(){
      display: none;
    }
  }
}

// App-specific tabs styling
.app-schedule-tabs {
  display: none;
}

// App-specific challenges list styling
.app-challenges-list {
  display: none;
}
</style>

<style lang="scss" scoped target="app">
// Mobile app-specific styling - SCOPED to this component only
.platform-ios, .platform-android {
  .schedule-table-wrap {
    background: $color-white;
    overflow: visible;
    padding: 0;
  }

  // Hide the web table in app context
  table.schedule-table {
    display: none !important;
  }

  // Show app tabs
  .app-schedule-tabs {
    display: flex !important;
    flex-shrink: 0;
    border-bottom: 1px solid $color-gray-light;
    background: $color-white;
    position: sticky;
    top: 0;
    z-index: 10;
    min-height: 48px;

    .tab {
      flex: 1;
      padding: $space-s $space-xxs;
      text-align: center;
      background: transparent;
      border: none;
      font-family: $font-condensed;
      font-size: $size-s;
      font-weight: bold;
      text-transform: uppercase;
      color: $color-gray-dark;
      cursor: pointer;
      position: relative;
      line-height: 1.2;
      min-height: 48px;

      &.active {
        color: $color-black;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: $color-red;
        }
      }
    }
  }

  // Show app challenges list
  .app-challenges-list {
    display: flex !important;
    flex-direction: column;
    gap: $space-s;
    padding: $space-s;
  }

  // Challenge card styling
  .challenge-card {
    display: flex;
    flex-direction: column;
    gap: $space-s;
    padding: $space-s;
    background: $color-white;
    border: 1px solid $color-gray-light;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .challenge-info {
    min-width: 0;
  }

  .challenge-name {
    margin: 0;
    font-family: $font-condensed;
    font-size: $size-s;
    font-weight: bold;
    text-transform: uppercase;
    color: $color-black;
    line-height: 1.3;
    word-wrap: break-word;
  }

  .challenge-action {
    flex-shrink: 0;
    width: 100%;
    --btn-width: 100%;
    --btn-text-transform: uppercase;
    --btn-font-size: #{$size-xs};
    --btn-font-weight: bold;
    --btn-padding: #{$space-s} #{$space-m};
  }

  .registered {
    --btn-background: #e8a598;
    --btn-color: #{$color-gray-dark};
    --btn-border: none;
  }

  .register-soon {
    --btn-background: #{$color-gray-light};
    --btn-color: #{$color-black};
    --btn-border: 1px solid #{$color-gray};
    cursor: not-allowed;
  }
}
</style>
